import { API_ACCESS_TOKEN, API_ENDPOINT, isRecruiterSide } from 'src/config/env.config';
import { baseApi } from './axios';
import {
	BaseAPIResponse,
	FilterUniversityStudentsInput,
	DevelopmentRoleType,
	GetCompletedMeetingCountAPIResponse,
	GetMultipleUsersByIdResponse,
	GetProfileScoreAPIResponse,
	GetUniversityStudentStatsAPIResponse,
	GetUserByIdAPIResponse,
	GetUserEligibilityAPIResponse,
	GetUsersAPIResponse,
	GetUsersForUniversityAPIResponse,
	GetUserVerificationAPIResponse,
	JapEnrollmentByIdAPIResponse,
	PreInterviewsFormData,
	ProfileFormData,
	RegisterAPIResponse,
	RegisterUserInput,
	ScreeningUsersFilterQueryInput,
	TechStackType,
	UpdateShowPostInterviewScheduleFlag,
	UpdateUserInput,
	UserCareerPathType,
	UserFilterQuery,
	UserRole,
	UserScreeningFeedback,
	DownloadUniversityStudentsAPIResponse,
	OfflineDocumentsTypes,
	SearchUserAPIResponse,
} from 'types';
import { GetJwtToken } from 'types';

const baseUrl = API_ENDPOINT;

export const getMultipleUsersById = async (data: { userIds: string[] }): Promise<GetMultipleUsersByIdResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/get-multiple-users-by-id`, data);
	return response.data;
};

export const updateRole = async (data: { newRole: UserRole }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/update-role`, data);
	return response.data;
};

export const sendOtp = async (args: { emailOrPhone: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/auth/send-otp`, {
		emailOrPhone: args.emailOrPhone,
	});
	return response.data;
};
export const linkLinkedInProfile = async (args: { linkedInProfileLink: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/connect-linked-in`, {
		linkedInProfileLink: args.linkedInProfileLink,
	});
	return response.data;
};
export const markOnboardingDone = async (): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/user/onboarding`);
	return response.data;
};
export const verifyOtp = async (args: {
	emailOrPhone: string;
	otp: string;
	otpSessionId: string;
	driveId?: string;
	driveLabel?: string;
	offlineDriveRegistration?: any;
	offlineDriveDocuments?: OfflineDocumentsTypes[];
}): Promise<BaseAPIResponse> => {
	console.log('args:', args);
	const response = await baseApi.post(`${baseUrl}/auth/verify-otp`, {
		emailOrPhone: args.emailOrPhone,
		otpSessionId: args.otpSessionId,
		otp: args.otp,
		driveId: args.driveId,
		driveLabel: args.driveLabel,
		offlineDriveRegistration: args?.offlineDriveRegistration,
		offlineDriveDocuments: args?.offlineDriveDocuments,
	});
	return response.data;
};
export const changeEmailOrPhone = async (args: {
	emailOrPhone: string;
	userId: string;
	isRecruiter: boolean;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/change-phone`, {
		emailOrPhone: args.emailOrPhone,
		userId: args.userId,
		isRecruiter: args.isRecruiter,
	});
	return response.data;
};

export const registerUser = async (args: RegisterUserInput): Promise<RegisterAPIResponse> => {
	const response = await baseApi.post(
		`${baseUrl}/auth/register`,
		{
			name: args.name,
			email: args.email,
			phone: args.phone,
			experience: args.experience,
			usedReferralCode: args.usedReferralCode,
			isRecruiter: isRecruiterSide ? true : false,
			company: args.companyName,
			experienceLevel: args.experienceLevel,
			designation: args.designation,
		},
		{ params: { frontendUrl: window.location.href, marketingPageUrl: localStorage.getItem('pageUrl') } },
	);
	return response.data;
};
export const verifyCookie = async ({
	jwtToken,
	jwtInternalStudentLinkToken,
}: {
	jwtToken?: string;
	jwtInternalStudentLinkToken?: string;
}): Promise<GetUserByIdAPIResponse> => {
	const token = jwtToken || jwtInternalStudentLinkToken;
	const response = await baseApi.get(`${baseUrl}/auth/get-user-details`, {
		...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
		params: {
			isOnwardStudent: !!jwtToken,
			isInternalStudent: !!jwtInternalStudentLinkToken,
		},
	});
	return response.data;
};
export const getUserById = async (id: string): Promise<GetUserByIdAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/${id}`);
	return response.data;
};
export const getPhoneVerificationStatus = async (emailOrPhone: string): Promise<GetUserVerificationAPIResponse> => {
	try {
		const response = await baseApi.get(`${baseUrl}/user/phone-verification`, {
			params: { emailOrPhone: emailOrPhone },
		});
		return response.data;
	} catch (e: any) {
		return { error: false, message: e.message };
	}
};
/**
 * The otpSessionId normally comes from the cached phone-verification query, but that query can
 * be empty (fetched before the OTP record existed, or failed and was swallowed above). Fetch it
 * fresh with a few retries so verify-otp is never sent with an empty session id.
 */
export const resolveOtpSessionId = async (emailOrPhone: string, cachedSessionId?: string): Promise<string> => {
	const fetchLatest = async () => (await getPhoneVerificationStatus(emailOrPhone))?.verification?.otpSessionId;
	// Always prefer the latest value so a stale session from a previous OTP is not reused.
	for (let attempt = 0; attempt < 3; attempt++) {
		const latest = await fetchLatest();
		if (latest) return latest;
		await new Promise((resolve) => setTimeout(resolve, 500));
	}
	return cachedSessionId || '';
};
export const verifyJwt = async (args: { token: string }): Promise<GetJwtToken> => {
	const response = await baseApi.post(`${baseUrl}/auth/verify-token`, {
		token: args.token,
	});
	return response.data;
};

export const updateLastUnseenMeetingId = async (
	lastUnseenMeetingId: string | null,
): Promise<BaseAPIResponse & { _id?: string }> => {
	const response = await baseApi.post(`${baseUrl}/user/updateLastUnseenMeetingId`, {
		lastUnseenMeetingId: lastUnseenMeetingId,
	});
	return response.data;
};
export const verifyPracticeJwt = async (args: { questionId: string; token: string }): Promise<GetJwtToken> => {
	const response = await baseApi.post(`${baseUrl}/auth/verify-practice-token`, {
		args,
	});
	return response.data;
};

export const getUserCompletedMeetingsCount = async (
	idOrHandler: string,
): Promise<GetCompletedMeetingCountAPIResponse> => {
	try {
		const response = await baseApi.get(`${baseUrl}/user/completedMeetingsCount/${idOrHandler}`);
		return response.data;
	} catch (e: any) {
		return { error: false, message: e.message };
	}
};
export const getPolicyForS3 = async (mime: any, userId: string, folderName: string) => {
	const data = { userId: userId, mime: mime, folderName: folderName, token: API_ACCESS_TOKEN };
	const response = await baseApi.post(`${baseUrl}/user/get-policy-for-s3`, {
		...data,
	});
	return response.data;
};

export const checkUserEligibility = async (): Promise<GetUserEligibilityAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/check-user-eligibility`);
	return response.data;
};
export const getUserProfileScores = async (handler: string): Promise<GetProfileScoreAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/profile-scores/${handler}`);
	return response.data;
};

export const getJapEnrollment = async (): Promise<JapEnrollmentByIdAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/jap-enrollment`);
	return response.data;
};
export const updateUserProfileDetails = async (payload: {
	id: string;
	data: PreInterviewsFormData;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/pre-interview/${payload.id}`, payload.data);
	return response.data;
};
export const updateUserScreeningFeedback = async (payload: {
	id: string;
	data: UserScreeningFeedback;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/updateScreeningFeedback/${payload.id}`, payload.data);
	return response.data;
};

export const updateUserById = async (payload: {
	id: string;
	data: UpdateUserInput;
	token?: string;
}): Promise<BaseAPIResponse> => {
	console.log('payload:', payload);
	const response = await baseApi.patch(`${baseUrl}/user/${payload.id}`, payload.data, {
		params: payload?.token ? { token: payload?.token } : {},
	});
	return response.data;
};

export const updateMentorById = async (payload: { id: string; data: UpdateUserInput }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/update/${payload.id}`, payload.data);
	return response.data;
};

export const getPolicyForS3NonVideo = async (
	fileName: string,
	fileLocation: any,
	mime: any,
	isPublic: boolean,
	token?: string,
) => {
	const data = { fileName, mime, fileLocation, isPublic };
	const response = await baseApi.post(
		`${baseUrl}/user/get-policy-for-s3-non-video`,
		{
			...data,
		},
		{
			params: {
				token,
			},
		},
	);
	return response.data;
};

export const getPolicyForS3NonVideoWithoutAuth = async (
	fileName: string,
	fileLocation: any,
	mime: any,
	isPublic: boolean,
	token?: string,
) => {
	const data = { fileName, mime, fileLocation, isPublic };
	const response = await baseApi.post(
		`${baseUrl}/user/get-policy-for-s3-non-video-without-auth`,
		{
			...data,
		},
		{
			params: {
				token,
			},
		},
	);
	return response.data;
};

export const getUniquePhoneNumber = async (
	emailOrPhone: string,
	userId: string,
): Promise<GetUserVerificationAPIResponse> => {
	try {
		const response = await baseApi.post(`${baseUrl}/user/unique-phone`, { phoneNumber: emailOrPhone, userId });
		return response.data;
	} catch (e: any) {
		return { error: false, message: e.message };
	}
};

export const getUniqueEmail = async (email: string, userId: string): Promise<GetUserVerificationAPIResponse> => {
	try {
		const response = await baseApi.post(`${baseUrl}/user/unique-email`, { email, userId });
		return response.data;
	} catch (e: any) {
		return { error: false, message: e.message };
	}
};

export const updateShowPostInterviewScheduleNudge = async (args: {
	id: string;
	payload: UpdateShowPostInterviewScheduleFlag;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/user/update-show-post-interview-schedule-nudge/${args.id}`, {
		...args.payload,
	});
	return response.data;
};

export const getUsersToBeScreened = async (
	filterData: ScreeningUsersFilterQueryInput,
	mentorId: string,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/users-to-be-screened/${mentorId}`, {
		...filterData,
	});
	return response.data;
};

export const requestCallback = async (userId: string): Promise<BaseAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/request-quick-callback/${userId}`);
	return response.data;
};

export const updateShowPostPostScreeningRoundNudge = async (): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/user/post-screening`);
	return response.data;
};

export const deleteAccount = async (): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/delete-account`);
	return response.data;
};
export const linkLMSAccount = async ({
	studentCode,
	jwtInternalStudentLinkToken,
}: {
	studentCode: string;
	jwtInternalStudentLinkToken: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(
		`${baseUrl}/user/link-lms-account`,
		{ studentCode },
		{
			headers: {
				Authorization: `Bearer ${jwtInternalStudentLinkToken}`,
			},
		},
	);
	return response.data;
};

export const connectWithLinkedIn = async (data: { linkedInProfileLink: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/connect-linked-in`, { ...data });
	return response.data;
};

export const getMyBDPocs = async (): Promise<BaseAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/my-bdpocs`);
	return response.data;
};

export const getAllAccountManagers = async (args: { filterQuery?: UserFilterQuery }): Promise<GetUsersAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/account-managers/all`, {
		params: { filterQuery: args.filterQuery },
	});
	return response.data;
};

export const getAllPrepCoaches = async (): Promise<GetUsersAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/all-prep-coaches`);
	return response.data;
};

export const getAllUserByUniversity = async (
	args: FilterUniversityStudentsInput,
): Promise<GetUsersForUniversityAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/all-by-university`, { params: args });
	return response.data;
};

export const downloadUniversityStudents = async (
	args: FilterUniversityStudentsInput,
): Promise<DownloadUniversityStudentsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/download-university-students`, { params: args });
	return response.data;
};

export const getUniversityStudentStats = async (): Promise<GetUniversityStudentStatsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/user/university-students-stats`);
	return response.data;
};

export const updateUserPreference = async (data: {
	careerPath?: UserCareerPathType;
	developmentRole?: DevelopmentRoleType;
	techStack?: TechStackType;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/updateUserPreference`, data);
	return response.data;
};

export enum HighestQualificationTypes {
	Twelfth = '12th',
	DiplomaAfterTenth = 'Diploma After 10th',
	DiplomaAfterTwelfth = 'Diploma After 12th',
	UnderGraduate = 'Under Graduate',
	PostGraduate = 'Post Graduate',
}

export const updateUserProfileData = async (data: ProfileFormData): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/update-user-profile-data`, data);
	return response.data;
};

export const acceptCodeOfConduct = async (data: {
	type: 'placement-drive' | 'assessment';
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/accept-code-of-conduct`, {}, { params: data });
	return response.data;
};

export const switchCollege = async (data: { college: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/switch-college`, data);
	return response.data;
};

export const searchUser = async (data: { searchType: string; searchText: string; role?: UserRole[] }): Promise<SearchUserAPIResponse> => {
	const response = await baseApi.get(
		`${baseUrl}/user/search-user?searchType=${data.searchType}&searchText=${data.searchText}${data.role ? `&role=${data.role.join(',')}` : ''}`,
	);
	return response.data;
};

export const updateCredentials = async (data: { email: string; phone: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/user/update-credentials`, data);
	return response.data;
}

export const resetOTPCount = async (data: { id: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/user/set-default-otp-count`, data);
	return response.data;
}

export const getApprovedBehaviouralFlags = async (userId: string): Promise<BaseAPIResponse & { flags?: any[] }> => {
	const response = await baseApi.get(`${baseUrl}/user/get-approved-behavioural-flags`, {
		params: { userId },
	});
	return response.data;
}


